import { IMapAdapter } from '../IMapAdapter.js';
import { ClassItem } from '../../model/ClassItem.js';
import { StyleOptions } from '../../model/StyleOptions.js';
import { LayerType } from '../../model/LayerOptions.js';
import { I18n } from '../../utils/i18n.js';

/**
 * Shared utility class for generating map legends
 * Works with both MapboxMap and MaplibreMap through the IMapAdapter interface
 */
export class LegendUtils {
  public static getLegendControl(mapAdapter: IMapAdapter) {
    const legend = document.createElement('div');
    legend.className = 'mapboxgl-ctrl mapboxgl-ctrl-group mapboxgl-ctrl-legend';
    legend.style.backgroundColor = '#ffffff8a';
    legend.style.marginLeft = '5px';
    legend.style.marginBottom = '5px';
    legend.style.padding = '5px';
    legend.style.overflowY = 'auto';
    legend.style.maxHeight = 'calc(100vh - 50px)';
    legend.innerHTML = '';
    legend.style.pointerEvents = 'auto';
    // Empêche la propagation des événements souris à la carte
    ['mousedown', 'mouseup', 'click', 'dblclick', 'mousemove', 'wheel', 'touchstart', 'touchend'].forEach((eventName) => {
      legend.addEventListener(eventName, (e) => {
        e.stopPropagation();
        e.preventDefault();
      });
    });

    let hasLegend = false;
    const layers = mapAdapter.getLayers();
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      const layerOptions = layer.getOptions();
      if (layerOptions.legend) {
        const ranges = layerOptions?.style?.ranges;
        const matches = layerOptions?.style?.matches?.classes;
        const colorScale = layerOptions?.style?.colorScale;
        if (ranges) {
          const legendItem = document.createElement('div');
          legendItem.id = 'legend-' + layer.getId();
          legendItem.className = 'mapboxgl-ctrl-legend-item';
          legendItem.innerHTML = `<span style="margin-left: 5px"><b>${ranges.getFieldAlias()}</b></span>
            ${LegendUtils.getCloroplethLegendHTML(ranges.classes)}`;
          legend.appendChild(legendItem);
          hasLegend = true;
          // Attach listeners directly without setTimeout to reduce overhead
          LegendUtils.listenLegendEvents(mapAdapter, ranges.classes, 'choropleth', legendItem);
        } else if (colorScale && Array.isArray(colorScale)) {
          const legendItem = document.createElement('div');
          legendItem.className = 'mapboxgl-ctrl-legend-item';
          legendItem.innerHTML = LegendUtils.getColorScaleLegendHTML(layerOptions.name ?? '', colorScale, layerOptions.style.colorScaleLabels);
          legend.appendChild(legendItem);
          hasLegend = true;
        } else if (matches) {
          const legendItem = document.createElement('div');
          legendItem.id = 'legend-' + layer.getId();
          legendItem.className = 'mapboxgl-ctrl-legend-item';
          legendItem.innerHTML = `
            ${LegendUtils.getMatchesLegendHTML(mapAdapter, matches)}`;
          legend.appendChild(legendItem);
          hasLegend = true;
          // Attach listeners directly without setTimeout to reduce overhead
          LegendUtils.listenLegendEvents(mapAdapter, matches, 'matches', legendItem);
        } else if (layerOptions.type === LayerType.Simple) {
          const legendItem = document.createElement('div');
          legendItem.id = 'legend-' + layer.getId();
          legendItem.className = 'mapboxgl-ctrl-legend-item';
          legendItem.innerHTML = LegendUtils.getSymbolLegendHTML(mapAdapter, layerOptions.style, layerOptions.name ?? '');
          legend.appendChild(legendItem);
          hasLegend = true;
        } else {
          console.warn('Legend not supported for this layer', layerOptions);
        }
      }
    }

    // Ajouter le contrôle personnalisé à la carte
    if (hasLegend) {
      const legendControl = {
        onAdd: () => legend,
        onRemove: () => {
          legend.parentNode?.removeChild(legend);
        },
      };
      //map.addControl(legendControl, 'bottom-left'); // Positionner le contrôle en haut à droite
      return legendControl;
    }
    return null;
  }

  public static getCloroplethLegendHTML(classes: ClassItem[]) {
    const nbClasses = classes.length;
    const lis = classes.map((c, i) => {
      if (c.label) {
        return this.getCloroplethElement(c);
      } else if (typeof c.value === 'undefined') {
        const label = `[ ${this.getClassValue({ value: c.minValue })} - ${this.getClassValue({ value: c.maxValue })} ]`;
        // Add label to the original item for later matching
        c.label = label;
        return this.getCloroplethElement(c);
      } else {
        if (i === 0) {
          return null;
        }
        const previousValue = this.getClassValue(classes[i - 1]);
        const currentValue = this.getClassValue(classes[i]);
        if (previousValue === currentValue) {
          return null;
        }
        const higherBracket = i === nbClasses - 1 ? '[' : ']';
        const label = `[ ${previousValue} - ${currentValue} ${higherBracket}`;
        // Add label to the original item for later matching
        c.label = label;
        return this.getCloroplethElement(c);
      }
    });

    let html = `<ul style="list-style-type: none;margin: 0;padding: 0;height:100%;vertical-align: middle;min-width: 120px;">
    ${lis.filter((l) => l !== null).join('')}
    </ul>`;

    return html;
  }

  private static getColorScaleLegendHTML(title: string, colorScale: string[], labels?: { low: string; high: string; }) {
    let html = `<span style="margin-left: 5px; white-space: nowrap;"><b>${title}</b></span>`;
    html += `<div style="display: flex; flex-direction: column; margin-top: 5px;">`;

    if (labels) {
      html += `<div style="display: flex; justify-content: space-between; width: 100%;">`;
      html += `<span>${labels.low}</span>`;
      html += `<span>${labels.high}</span>`;
      html += `</div>`;
    }

    html += `<div style="display: flex; flex-direction: row; flex-grow: 1; height: 18px; border: solid rgba(255,255,255,203) 1px; border-radius: 5px; min-width: 120px;">`;
    colorScale.forEach(color => {
      html += `<div style="background-color: ${color}; flex-grow: 1; height: 100%;"></div>`;
    });
    html += `</div>`;

    html += `</div>`;
    return html;
  }

  private static getCloroplethElement(classItem: ClassItem): string {
    const hiddenStyle = classItem.hidden ? 'opacity: 0.5; filter: grayscale(1);' : '';
    const i18n = I18n.getInstance();
    const tooltipText = classItem.hidden ? i18n.t('legend.tooltip.clickToShow') : i18n.t('legend.tooltip.clickToHide');

    return `<li class="li-choropleth" title="${tooltipText}" style="height:22px;display:flex;cursor: pointer;align-content:center;align-items: center;margin-bottom: 1px; ${hiddenStyle}">
              <div class="fill-symbol" style="display:inline-block;
                margin-right:5px;
                background-color: ${classItem.color};
                border: solid rgba(255,255,255,203) 1px;
                border-radius: 5px;
                width: 30px; height: 18px;">
              </div>
              <span style="margin-left:10px; white-space: nowrap;">${classItem.label}</span>
          </li>`;
  }

  public static getMatchesLegendHTML(mapAdapter: IMapAdapter, classes: ClassItem[]) {
    const i18n = I18n.getInstance();

    const lis = classes.map((c, i) => {
      const imageUrl = mapAdapter.getImage(c.value);
      const hiddenStyle = c.hidden ? 'opacity: 0.5; filter: grayscale(1);' : '';
      const tooltipText = c.hidden ? i18n.t('legend.tooltip.clickToShow') : i18n.t('legend.tooltip.clickToHide');

      return `<li class="li-match" title="${tooltipText}" style="height:22px;display:flex;cursor: pointer;align-content:center;align-items: center;margin-bottom: 1px; ${hiddenStyle}"
              >
                <img src="${imageUrl}" alt="${c.value}" style="width: 20px; height: 20px; margin-right: 5px;" />
                <span style="margin-left:10px">${c.value}</span>
            </li>`;
    });

    let html = `<ul style="list-style-type: none;margin: 0;padding: 0;height:100%;vertical-align: middle;min-width: 120px;">
    ${lis.filter((l) => l !== null).join('')}
    </ul>`;

    return html;
  }

  public static getSymbolLegendHTML(mapAdapter: IMapAdapter, symbol: StyleOptions, label: string) {
    if (symbol.images?.length && symbol.images.length > 0) {
      const imageUrl = symbol.images[0].url;
      return `<div style="display:flex;align-content:center;align-items: center;margin-bottom: 1px;">
                <img src="${imageUrl}" alt="${symbol.images[0]}" style="width: 20px; height: 20px; margin-right: 5px;" />
                <span style="margin-left:10px">${label}</span>
            </div>`;
    } else if (symbol.fillColor) {
      return `<div style="display:flex;align-content:center;align-items: center;margin-bottom: 1px;">
                <div class="fill-symbol" style="display:inline-block;
                  margin-right:5px;
                  background-color: ${symbol.fillColor};
                  border: solid rgba(255,255,255,203) 1px;
                  border-radius: 5px;
                  width: 30px; height: 18px;">
                </div>
                <span style="margin-left:10px">${label}</span>
            </div>`;
    }
    return '';
  }

  private static listenLegendEvents(mapAdapter: IMapAdapter, items: ClassItem[], type: 'matches' | 'choropleth', container?: HTMLElement) {
    const selector = type === 'matches' ? '.li-match' : '.li-choropleth';
    const searchContext = container || document;
    searchContext.querySelectorAll(selector).forEach((li) => {
      li.addEventListener('click', (e) => {
        console.log('Legend item clicked:', type, li.textContent?.trim());
        const classValue = li.textContent?.trim();
        const classItem = items.find((item) => this.getClassValue(item) === classValue);
        if (classItem) {
          console.log('Class item found:', classItem);
          this.toggleClassVisibility(mapAdapter, classItem, type);
        } else {
          console.warn('Class item not found for value:', classValue);
        }
      });
    });
  }

  private static toggleClassVisibility(mapAdapter: IMapAdapter, classItem: ClassItem, type: 'matches' | 'choropleth') {
    const map = mapAdapter.getMapBoxMap();
    const layers = mapAdapter.getLayers();
    const classValue = this.getClassValue(classItem);
    layers.forEach((layer) => {
      const layerOptions = layer.getOptions();
      if (layerOptions.legend) {
        if (type === 'matches' && layerOptions.style?.matches?.classes) {
          // Handle matches (discrete values)
          for (let c of layerOptions.style.matches.classes) {
            if (c.hidden === undefined) c.hidden = false;
            if (this.getClassValue(c) === this.getClassValue(classItem)) {
              classItem.hidden = !classItem.hidden;
            }
          }
          const hiddenValues = layerOptions.style.matches.classes.filter((c) => c.hidden).map((c) => this.getClassValue(c));
          if (hiddenValues.length === 0) {
            layer.setFilter(() => true);
          } else {
            const field = layerOptions.style.matches?.field;
            if (field) {
              layer.setFilter((properties) => hiddenValues.indexOf(properties[field]) === -1);
            } else {
              layer.setFilter(() => true);
            }
          }

          const legendElt = document.getElementById('legend-' + layer.getId());
          if (legendElt) {
            legendElt.innerHTML = LegendUtils.getMatchesLegendHTML(mapAdapter, layerOptions.style?.matches.classes);
            LegendUtils.listenLegendEvents(mapAdapter, layerOptions.style?.matches.classes, 'matches', legendElt as HTMLElement);
          }
        } else if (type === 'choropleth' && layerOptions.style?.ranges) {
          // Handle ranges (continuous values)
          for (let c of layerOptions.style.ranges.classes) {
            if (c.hidden === undefined) c.hidden = false;
            if (this.getClassValue(c) === this.getClassValue(classItem)) {
              classItem.hidden = !classItem.hidden;
            }
          }

          const field = layerOptions.style.ranges.field;
          if (field) {
            const hiddenRanges = layerOptions.style.ranges.classes.filter((c) => c.hidden);
            if (hiddenRanges.length === 0) {
              layer.setFilter(() => true);
            } else {
              // Filter based on ranges - exclude values that fall within hidden ranges
              layer.setFilter((properties) => {
                const value = properties[field];
                if (value === undefined || value === null) return true;

                // Check if value falls within any hidden range
                for (let range of hiddenRanges) {
                  if (range.minValue !== undefined && range.maxValue !== undefined) {
                    if (value >= range.minValue && value < range.maxValue) {
                      return false; // Value is in hidden range
                    }
                  } else if (range.value !== undefined) {
                    if (value === range.value) {
                      return false; // Exact value match
                    }
                  }
                }
                return true; // Value is not in any hidden range
              });
            }
          }

          const legendElt = document.getElementById('legend-' + layer.getId());
          if (legendElt) {
            const titleHtml = `<span style="margin-left: 5px"><b>${layerOptions.style.ranges.getFieldAlias()}</b></span>`;
            legendElt.innerHTML = titleHtml + LegendUtils.getCloroplethLegendHTML(layerOptions.style.ranges.classes);
            LegendUtils.listenLegendEvents(mapAdapter, layerOptions.style.ranges.classes, 'choropleth', legendElt as HTMLElement);
          }
        }
      }
    });
  }

  private static getClassValue(classItem: ClassItem): string {
    const value = classItem.label ? classItem.label : classItem.value;
    if (typeof value === 'number') {
      return new Intl.NumberFormat(navigator.language, { maximumFractionDigits: 1 }).format(value);
    }
    return value;
  }
}
